<template lang="pug">
  include TableRowBase.pug
</template>

<script>
  import lodash from 'lodash';
  import { mapGetters } from 'vuex';
  import DataMethods from '../../../utils/DataMethods.js';

  export default {
    name: 'TableRowBase',
    mixins: [DataMethods],
    props: ['metric'],
    computed: {
      ...mapGetters('allergyKpis', [
        'benchmarkYear',
        'benchmarkMonth',
        'curYear',
        'curMonth',
        'prevYear',
        'prevYearMonth',
        'month',
        'prevYearNull',
        'compMode',
        'comments',
        'ytdCustomCalculationFields',
      ]),
      rowClass() {
        return this.field;
      },
      name() {
        return this.metric.name;
      },
      field() {
        return this.metric.field;
      },
      type() {
        return this.metric.type;
      },
      aggregateType() {
        return this.metric.aggregateType;
      },
      higherIsBetter() {
        return this.metric.higherIsBetter || 1;
      },
      actualValueRaw() {
        return this.curMonth[this.field] || 0;
      },
      actualValue() {
        return this.formatData(this.actualValueRaw);
      },
      benchmarkValue() {
        return this.formatData(this.benchmarkValueRaw);
      },
      benchmarkValueRaw() {
        return this.benchmarkMonth[this.field];
      },
      previousValue() {
        return this.prevYearNull ? '-' : this.formatData(this.previousValueRaw);
      },
      previousValueRaw() {
        return this.prevYearMonth[this.field] || 0;
      },
      benchmarkVariance() {
        return this.actualValueRaw - this.benchmarkValueRaw;
      },
      benchmarkVarianceInt() {
        return this.getVarianceInt(this.benchmarkVariance);
      },
      benchmarkValueDiff() {
        return (this.benchmarkValueRaw === '-') ? '-' : this.getValueDiff(this.actualValueRaw, this.benchmarkValueRaw);
      },
      benchmarkVarianceClass() {
        if (this.benchmarkValueDiff === '-') {
          return '';
        } else if (this.benchmarkVarianceInt === 1) {
          return 'kpi-performance-table__metric--positive';
        }
        if (this.benchmarkVarianceInt == -1) {
          return 'kpi-performance-table__metric--negative';
        }
        return '';
      },
      previousYearVariance() {
        return (this.actualValue === '-') ? 0 : this.actualValueRaw - this.previousValueRaw;
      },
      previousYearVarianceInt() {
        return this.getVarianceInt(this.previousYearVariance);
      },
      previousYearValueDiff() {
        return this.prevYearNull ? 'N/A' : this.getValueDiff(this.actualValueRaw, this.previousValueRaw);
      },
      previousYearVarianceClass() {
        if (this.previousYearVarianceInt === 1) {
          return 'kpi-performance-table__metric--positive';
        }
        if (this.previousYearVarianceInt == -1) {
          return 'kpi-performance-table__metric--negative';
        }
        return '';
      },
      actualYtd() {
        return this.formatData(this.actualYtdRaw);
      },
      actualYtdRaw() {
        return this.getYtdAdjusted(this.curYear, this.month);
      },
      benchmarkYtd() {
        return this.formatData(this.benchmarkYtdRaw);
      },
      benchmarkYtdRaw() {
        return this.getYtdAdjusted(this.benchmarkYear, this.month);

        // if (this.type == 'percent' || this.aggregateType == 'avg') {
        //   return this.benchmark[this.field];
        // }
        // return this.benchmark[this.field] * (this.month);
      },
      previousYtd() {
        return this.prevYearNull ? '-' : this.formatData(this.previousYtdRaw);
      },
      previousYtdRaw() {
        return this.getYtdAdjusted(this.prevYear, this.month);
      },
      benchmarkYtdVariance() {
        return this.actualYtdRaw - this.benchmarkYtdRaw;
      },
      benchmarkYtdVarianceInt() {
        return this.getVarianceInt(this.benchmarkYtdVariance);
      },
      benchmarkYtdValueDiff() {
        return this.benchmarkYtdRaw === 0 ? '-' : this.getValueDiff(this.actualYtdRaw, this.benchmarkYtdRaw);
      },
      benchmarkYtdVarianceClass() {
        if (this.benchmarkYtdValueDiff === '-') {
          return '';
        } else if (this.benchmarkYtdVarianceInt === 1) {
          return 'kpi-performance-table__metric--positive';
        }
        if (this.benchmarkYtdVarianceInt == -1) {
          return 'kpi-performance-table__metric--negative';
        }
        return '';
      },
      previousYtdVariance() {
        return this.actualYtdRaw - this.previousYtdRaw;
      },
      previousYtdVarianceInt() {
        return this.getVarianceInt(this.previousYtdVariance);
      },
      previousYtdValueDiff() {
        return this.prevYearNull ? 'N/A' : this.getValueDiff(this.actualYtdRaw, this.previousYtdRaw);
      },
      previousYtdVarianceClass() {
        if (this.previousYtdVarianceInt === 1) {
          return 'kpi-performance-table__metric--positive';
        }
        if (this.previousYtdVarianceInt == -1) {
          return 'kpi-performance-table__metric--negative';
        }
        return '';
      },
      theComment() {
        return lodash.find(this.comments, (comment) => comment.type === this.metric.name);
      },
      comment() {
        if (this.theComment != undefined) {
          return this.theComment.note + "<br />" + ' - ' + this.theComment.person;
        }
        return '';
      },
      commentShort() {
        if (this.comment && this.comment.length > 45) {
          return this.comment.substr(0, 45 - 3) + '...';
        }
        return this.comment;
      },
    },
    methods: {

      /**
       *  Returns an integer representing the status of a variance.
       *  For example, you call getVarianceInt(a-b, -1). If "a" is greater than "b",
       *  then this will return -1 because the 2nd param denotes that higher numbers are worse.
       *
       *  Calling getVarianceInt(a-b) where "a" is greater than "b" will return 1, because
       *  leaving the 2nd param blank defaults to higher numbers being better.
       *
       *  @param variance A numeric value representing the result of some calculation.
       *  @param higherIsBetter A value of 1, 0, or -1, that denotes whether higher values are better, neutral, or worse.
       *  @return A value between -1 and 1.
       */
      getVarianceInt(variance) {
        if (this.higherIsBetter == 0 || variance == 0) {
          return 0;
        }
        // If variance is a positive number (Actual > Target)
        if (variance > 0) {
          return (this.higherIsBetter > 0) ? 1 : (-1);
        }
        // If variance is a negative number (Target > Actual)
        return (this.higherIsBetter > 0) ? (-1) : 1;
      },

      getValueDiff(value1, value2) {
        if (this.compMode == 'percent' && this.type != 'percent') {
          var result = this.round(
            this.getPercent((value1 - value2) / ((value2 != 0) ? value2 : 1))
          );
          if (result >= 0) {
            result = '+' + result;
          }
          return result + '%';
        }
        return this.formatData(value1 - value2);
      },

      /**
       *  Give aggregate totals of a KPI. If given through month, will limit sum
       *  from January through the month given, otherwise sums all available months.
       *
       *  @param yearObj A KPI report in json format.
       *  @param throughMonth A month to limit the summation to. Valid numbers are 1-12.
       *  @param offset Offset to start at.
       *  @return A numeric value.
       */
      // getYtd(yearObj, throughMonth = false, offset = 0) {
      //   return this.getYtdByField(yearObj, this.field, throughMonth, offset);
      // },

      /**
       *  Give aggregate totals of a KPI. If given through month, will limit sum
       *  from January through the month given, otherwise sums all available months.
       *
       *  @param yearObj A KPI report in json format.
       *  @param field The KPI to sum denoted by it's field name.
       *  @param throughMonth A month to limit the summation to. Valid numbers are 1-12.
       *  @param offset Offset to start at.
       *  @return A numeric value.
       */
      getYtdByField(yearObj, field, throughMonth = false, offset = 0) {
        throughMonth = throughMonth || this.month;
        // Check if this field is one with a special calculation.
        if (field === this.field && this.ytdCustomCalculationFields.includes(field)) {
          return this.getCalculatedYtd(yearObj, throughMonth, offset);
        }

        // Otherwise assume it's a simple field that just needs to summed up.
        const ytdValue = lodash.reduce(yearObj, (sum, monthVal, index) => {
          if (index >= offset && (!throughMonth || throughMonth > index)) {
            if (monthVal[field] != undefined && monthVal[field] != null) {
              return sum + parseInt(monthVal[field]);
            }
          }
          return sum;
        }, 0);

        // Check if this YTD value is an average rather than sum
        return (this.aggregateType == 'avg') ? ytdValue / (throughMonth - offset) : ytdValue;
      },

      /**
       *  Same as getYtd above, except will average the sum of aggregate metrics.
       *  Other functions rely on getYtd to provide a SUM, even if the aggregate type is "avg".
       *  Some examples are calcTotals() and getCalculatedYtd().
       *  Furthermore, it might make sense to avoid duplication and just call getYtd from this method
       *  to get a SUM, then divide. However, the division doesn't happen for all "avg" aggregate types.
       *  It only applies to those that aren't pre-calculated. Thus getCalc would have to return both a
       *  summed value and a boolean indicating whether the SUM was pre-calculated or not.
       *  To avoid having to return an array, I'm just duplicating the code here.
       */
      getYtdAdjusted(yearObj, throughMonth = false, offset = 0) {
        throughMonth = throughMonth || this.month;
        // Check if this field is one with a special calculation.
        // Check if this field is one with a special calculation.
        if (this.ytdCustomCalculationFields.includes(this.field)) {
          return this.getCalculatedYtd(yearObj, throughMonth, offset);
        }

        // Otherwise assume it's a simple field that just needs to summed up.
        const ytdValue = lodash.reduce(yearObj, (sum, monthVal, index) => {
          if (index >= offset && (throughMonth > index)) {
            if (monthVal[this.field] != undefined && monthVal[this.field] != null) {
              return sum + parseInt(monthVal[this.field]);
            }
          }
          return sum;
        }, 0);


        // Check if this YTD value is an average rather than sum
        if (this.aggregateType == 'avg') {
          return ytdValue / (throughMonth - offset);
        }
        return ytdValue;
      },

      /**
       *  Calculation Methods
       */
      formatData(value) {
        // Customize return value depending on metric
        if (this.type == 'money') {
          return this.currency(value, '$');
        } else if (this.type == 'unit') {
          return this.currency(value);
        } else if (this.type == 'percent') {
          return this.round(this.getPercent(value), 2) + '%';
        }
        return value;
      },

      /**
       *  Given and array of KPI month objects, a field to look at, and an optional throughMonth,
       *  Returns the YTD value of that field if its a calculated property, and false otherwise.
       *  Overridden by components that have a special calc
       *
       *  @param yearObj An array of KPI month objects.
       *  @param throughMonth (optional), A number between 1 and 12.
       */
      getCalculatedYtd(yearObj, throughMonth) {
        return false;
      },

      /**
       *  Method that helps avoid dividing by zero.
       *
       *  @param numerator A numeric value.
       *  @param denominator A numeric value.
       *  @param returnValueIfDenominatorIsInvalid Optional value to return if division by zero occurs.
       *  @return A ratio.
       */
      getRatio(numerator, denominator, returnValueIfDenominatorIsInvalid = false) {
        if (this.empty(parseFloat(denominator))) {
          return (returnValueIfDenominatorIsInvalid !== false) ? returnValueIfDenominatorIsInvalid : '';
        }
        if (this.empty(parseFloat(numerator))) {
          return 0;
        }
        return numerator / denominator;
      },

      /**
       *  Emulates PHP's empty() method.
       *
       *  @param data (mixed) A variable you want to determine if its empty.
       *  @return boolean
       */
      empty(data) {
        // If data is a number
        if (typeof (data) == 'number' || typeof (data) == 'boolean') {
          return (data == 0);
        }
        // If data is a string literally representing zero
        if (data == '0') {
          return true;
        }
        // All other cases (functions, regular expressions, elements, documents, etc)
        return lodash.isEmpty(data);
      },
    },
  };
</script>
